import { CheckIcon, TriangleDashed, X } from "lucide-react";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import HalfFilledTriangleIcon from "@/assets/icons/half-filled-triangle-icon";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import {
  ESTIMATE_POINTS,
  ESTIMATE_TO_TSHIRT_SIZE,
  getTShirtSize,
} from "@/types/issues";

interface EstimateBadgeProps {
  estimate?: string | number | null;
  className?: string;
  issueId?: string;
  variant?: "outline" | "default" | "text-dark";
  onEstimateChange?: (estimate: number | null) => void;
  iconColor?: string;
  borderless?: boolean;
  iconOnly?: boolean;
}

export const EstimateBadge: React.FC<EstimateBadgeProps> = ({
  estimate,
  className = "",
  issueId,
  variant = "default",
  onEstimateChange,
  iconColor = "var(--text-primary)",
  borderless = false,
  iconOnly = false,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const updateIssueEstimate = useIssuesStore(
    (state) => state.updateIssueEstimate,
  );
  const searchInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
  const filteredEstimates = ESTIMATE_POINTS.filter((points) =>
    ESTIMATE_TO_TSHIRT_SIZE[points]
      .toLowerCase()
      .includes(searchQuery.toLowerCase()),
  );

  useEffect(() => {
    if (isOpen) {
      const timeoutId = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timeoutId);
    } else {
      setSearchQuery("");
    }
  }, [isOpen]);

  const handleEstimateChange = async (
    e: React.MouseEvent,
    points: number | undefined,
  ) => {
    e.stopPropagation();

    if (onEstimateChange) {
      onEstimateChange(points ?? null);
      return;
    }

    if (!issueId) {
      toast.error("Could not update estimate");
      return;
    }

    try {
      updateIssueEstimate(supabaseClient, issueId, points ?? null);
      toast.success(
        points === undefined
          ? "Estimate removed"
          : `Estimate set to ${ESTIMATE_TO_TSHIRT_SIZE[points]}`,
      );
    } catch (error) {
      console.error("Failed to update estimate:", error);
      toast.error("Failed to update estimate");
    }
  };

  const BadgeContent = () => (
    <div className="flex items-center gap-1">
      {!estimate ? (
        <TriangleDashed className="w-3.5 h-3.5 shrink-0" color={iconColor} />
      ) : (
        <>
          <HalfFilledTriangleIcon className="w-3.5 h-3.5 shrink-0" />
          {!iconOnly && (
            <span className="text-white">
              {getTShirtSize(Number(estimate))}
            </span>
          )}
        </>
      )}
    </div>
  );

  const baseSpanClassName = `inline-flex items-center justify-center rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 text-xs text-secondary-n-400 cursor-pointer ${
    !borderless ? "border" : ""
  }`;

  const textDarkDropdownNode = (
    <button
      className={`${baseSpanClassName} ${className}`}
      data-state="closed"
      aria-label={`Estimate: ${
        estimate ? ESTIMATE_TO_TSHIRT_SIZE[Number(estimate)] : "No estimate"
      }`}
    >
      {estimate ? (
        <span
          className={`${baseSpanClassName} gap-1 w-full h-7 px-2 bg-secondary-0 hover:bg-secondary-n-800 ${
            !borderless ? "border-border-primary" : ""
          }`}
        >
          <HalfFilledTriangleIcon className="w-3.5 h-3.5 shrink-0" />
          <span className="text-xs font-medium text-text-primary whitespace-nowrap">
            {ESTIMATE_TO_TSHIRT_SIZE[Number(estimate)]}
          </span>
        </span>
      ) : (
        <span
          className={`${baseSpanClassName} gap-1 w-full h-7 px-2 bg-secondary-0 hover:bg-secondary-n-800 ${
            !borderless ? "border-secondary-n-900" : ""
          }`}
        >
          <TriangleDashed className="w-3.5 h-3.5 shrink-0" color={iconColor} />
          {!iconOnly && (
            <span className="text-xs font-medium text-secondary-n-400 whitespace-nowrap">
              Estimate
            </span>
          )}
        </span>
      )}
    </button>
  );

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <div>
          {variant === "text-dark" ? (
            textDarkDropdownNode
          ) : (
            <Badge
              variant="outline"
              className={`h-6 w-fit py-0 px-1.5 text-xs font-normal flex items-center text-muted-foreground transition-colors hover:bg-secondary-n-800 cursor-pointer bg-secondary-n-950 ${
                iconOnly && !estimate ? "px-1" : ""
              } ${className}`}
            >
              <BadgeContent />
            </Badge>
          )}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        ref={menuRef}
        className="w-[200px] overflow-hidden bg-secondary-0 rounded-lg"
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          searchInputRef.current?.focus();
        }}
        onPointerDownOutside={(e) => {
          if (!menuRef.current?.contains(e.target as Node)) {
            setIsOpen(false);
          }
        }}
        onInteractOutside={(e) => {
          if (searchInputRef.current?.contains(e.target as Node)) {
            e.preventDefault();
          }
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-1">
          <div className="relative">
            <Input
              ref={searchInputRef}
              placeholder="Change estimate"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-7 text-sm bg-secondary-n-950 border-0! border-none! focus:border-0! focus:border-none! focus-visible:border-0! focus-visible:border-none! focus-visible:ring-0! focus-visible:!ring-none placeholder:text-secondary-n-600 pr-6"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onBlur={(e) => {
                if (menuRef.current?.contains(e.relatedTarget as Node)) {
                  e.preventDefault();
                  searchInputRef.current?.focus();
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.stopPropagation();
                }
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="size-4" />
              </button>
            )}
          </div>
        </div>
        <DropdownMenuSeparator />
        <div
          className="overflow-y-auto overflow-x-hidden context-menu-scroll"
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(255, 255, 255, 0.3) transparent",
          }}
        >
          <DropdownMenuItem onClick={(e) => handleEstimateChange(e, undefined)}>
            <div className="flex items-center justify-between w-full py-1 pl-2">
              <div className="flex items-center gap-2">
                <TriangleDashed className="w-3.5 h-3.5 shrink-0" />
                <span>No estimate</span>
              </div>
              {!estimate && <CheckIcon className="size-3.5" color="#F5F5F6" />}
            </div>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="caption text-2xs font-medium text-secondary-n-400 ml-2">
            Estimates
          </DropdownMenuLabel>
          {filteredEstimates.map((points) => (
            <DropdownMenuItem
              key={points}
              onClick={(e) => handleEstimateChange(e, points)}
            >
              <div className="flex items-center justify-between w-full py-1 pl-2">
                <div className="flex items-center gap-2">
                  <HalfFilledTriangleIcon className="w-3.5 h-3.5 shrink-0" />
                  <span>{ESTIMATE_TO_TSHIRT_SIZE[points]}</span>
                </div>
                {Number(estimate) === points && (
                  <CheckIcon className="size-3.5" color="#F5F5F6" />
                )}
              </div>
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default EstimateBadge;
